import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { getNetworkLabel } from '@/utils/dq';
import { EditorialFacebookPostNetwork } from '@/utils/enums';

import EnablingModal from '../EnablingModal';
import AuthorizationButton from './AuthorizationButton';
import { PageOverview } from './Modal';

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Thumbnail = styled.img.attrs({ loading: 'lazy' })`
  width: 16px;
  height: 16px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface ActionButtonProps {
  $btn?: 'green' | 'red' | 'purple' | 'gray';
}

const ActionButton = styled(Text).attrs({
  $type: 'smallHeadline',
  as: 'button',
  type: 'button',
})<ActionButtonProps>`
  height: 32px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  justify-content: center;
  border: 1px solid transparent;

  ${({ theme, $btn = 'purple' }) => {
    switch ($btn) {
      case 'purple':
        return css`
          width: 108px;
          color: ${theme.colors.purple};
          background: #f1f0fb;
          &:hover {
            border-color: ${theme.colors.purple};
          }
        `;
      case 'red':
        return css`
          width: 108px;
          color: ${theme.colors.error};
          background: ${theme.colors.errorLight};
          &:hover {
            border-color: ${theme.colors.error};
          }
        `;
      case 'green':
        return css`
          width: 108px;
          color: ${theme.colors.success};
          background: #ecf9f1;
          &:hover {
            border-color: ${theme.colors.success};
          }
        `;
      case 'gray':
        return css`
          width: 30px;
          color: ${theme.colors.black};
          background: ${theme.colors.white};
          border-color: ${theme.colors.lightGray};
          &:hover {
            border-color: rgba(34, 34, 34, 0.25);
          }
          > svg {
            width: 16px;
          }
        `;
    }
  }};
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

interface Props {
  page: PageOverview;
  network: EditorialFacebookPostNetwork;
  onClose: VoidFunction;
}

const TableRow: FC<Props> = ({ page, network, onClose }) => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onOpenPage = () => {
    navigate(
      `/general/editorial-distribution-queue/${page.id}/queued/${getNetworkLabel(network)}`,
    );

    onClose();
  };

  const onQueueToggle = async () => {
    if (!page.frequency) {
      return alerts.error(
        'Frequency settings is not provided for this page/profile!',
        page.name,
      );
    }

    if (page.queueEnabled) {
      try {
        const confirmed = await alerts.confirm(
          'Are you sure?',
          `Disable distribution queue for ${page.name}`,
        );

        if (!confirmed) {
          return;
        }

        await api.post(`/v1/editorial/queue/status`, {
          network,
          page_id: page.id,
          enabled: false,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        alerts.close();
      } catch (_) {
        alerts.error();
      }

      return;
    }

    setShowModal(true);
  };

  return (
    <tr>
      <Td>
        <Name>
          {!!page.thumbnail && (
            <Thumbnail src={page.thumbnail} alt={page.name} />
          )}
          {page.name}
        </Name>
      </Td>
      <Td>{page.draftPostsCount ?? '-'}</Td>
      <Td>{page.frequency ?? '-'}</Td>
      <Td>{page.failedPostsCount ?? '-'}</Td>
      <Td>{page.deletedPostsCount ?? '-'}</Td>
      <Td>
        {page.estimatedEndTime ? (
          <>
            {DateTime.fromISO(page.estimatedEndTime).toLocaleString()} <br />
            <Time $color="gray" $type="extraSmall">
              {DateTime.fromISO(page.estimatedEndTime).toFormat('HH:mm')}
            </Time>
          </>
        ) : (
          <>
            -/-/- <br />
            <Time $color="gray" $type="extraSmall">
              -
            </Time>
          </>
        )}
      </Td>
      <Td>
        <Actions>
          <ActionsCol>
            <ActionButton onClick={onOpenPage}>Open page</ActionButton>
            {page.queueEnabled ? (
              <ActionButton $btn="red" as="button" onClick={onQueueToggle}>
                Disable queue
              </ActionButton>
            ) : (
              <ActionButton $btn="green" onClick={onQueueToggle} as="button">
                Enable queue
              </ActionButton>
            )}
          </ActionsCol>

          {network === EditorialFacebookPostNetwork.FACEBOOK && (
            <ActionsCol>
              <AuthorizationButton
                page={page}
                network={EditorialFacebookPostNetwork.PINTEREST}
              />
              <AuthorizationButton
                page={page}
                network={EditorialFacebookPostNetwork.TWITTER}
              />
              <AuthorizationButton
                page={page}
                network={EditorialFacebookPostNetwork.LINKEDIN}
              />
              <AuthorizationButton
                page={page}
                network={EditorialFacebookPostNetwork.TIKTOK}
              />
            </ActionsCol>
          )}
          {network === EditorialFacebookPostNetwork.INSTAGRAM && (
            <AuthorizationButton
              page={page}
              network={EditorialFacebookPostNetwork.THREADS}
            />
          )}
          <AnimatePresence>
            {showModal && (
              <EnablingModal
                onClose={() => setShowModal(false)}
                channelId={page.id}
                frequency={page.frequency!}
                network={network}
              />
            )}
          </AnimatePresence>
        </Actions>
      </Td>
    </tr>
  );
};

export default TableRow;
